import {
  Avatar,
  Button,
  Chip,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatDate, getPlanColor, getPlanLabel, buttonStyle } from './types';
import { EmptyState } from '../../assets/icons/Index';
import CloseConsultancyModal from '../../components/specific/modal/CloseConsultancyModal';
import { useState, useMemo, useEffect } from 'react';
import EmailIcon from '@mui/icons-material/Email';
import axios from 'axios';
import { ConsultancyProps, ConsultancyStatus } from '../../interfaces/Consultancy';
import { useParams } from 'react-router-dom';
import { useGlobalData } from '../../hooks/globalDataContext';
import { notifyCreditsUpdated } from '../../utils/creditsEvents';
import Loader from '../../assets/gifs/loader.gif'
import { getConfig } from '../../config/env';

const Consultancy = () => {
  const url = getConfig().BASE_URL;
  const { t, i18n } = useTranslation();
  const { userId } = useParams();
  const authToken = sessionStorage.getItem('authToken');
  const { refreshCredits } = useGlobalData();
  const [open, setOpen] = useState<boolean>(false);
  const [selectedConsultancyId, setSelectedConsultancyId] = useState<number | undefined>(undefined);
  const [paymentId, setPaymentId] = useState<number | undefined>(undefined);
  const [pinnerFirstName, setPinnerFirstName] = useState<string>('');
  const [consultancies, setConsultancies] = useState<ConsultancyProps[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchConsultancies = async () => {
      setLoading(true);
      const response = await axios.get(`${url}/users/pinnerpro/${userId}/scheduled-consultancies/`, {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });
      setConsultancies(response.data);
      setLoading(false);
    };

    fetchConsultancies();
  }, [authToken, userId]);

  const sortedConsultancies = useMemo(() => {
    return [...consultancies].sort((pending, completed) => {
      const dateA = new Date(pending.purchased_at);
      const dateB = new Date(completed.purchased_at);

      if (pending.status !== completed.status) {
        return pending.status === ConsultancyStatus.PENDING ? -1 : 1;
      }

      if (pending.status === ConsultancyStatus.PENDING) {
        return dateA.getTime() - dateB.getTime();
      } else {
        return dateB.getTime() - dateA.getTime();
      }
    });
  }, [consultancies]);

  const handleStatusClick = (status: ConsultancyStatus, consultancyId: number, paymentId: number, pinnerFirstName: string) => {
    if (status === ConsultancyStatus.PENDING) {
      setSelectedConsultancyId(consultancyId);
      setPaymentId(paymentId);
      setPinnerFirstName(pinnerFirstName);
      setOpen(true);
    }
  };

  const handleConsultancyClosed = (consultancyId: number) => {
    setConsultancies(prevConsultancies =>
      prevConsultancies.map(consultancy =>
        consultancy.id === consultancyId
          ? { ...consultancy, status: ConsultancyStatus.COMPLETED }
          : consultancy
      )
    );

    // Aggiorna i crediti e notifica altri componenti
    refreshCredits(true);
    notifyCreditsUpdated();
  };

  return (
    <div className='w-full min-h-screen p-6 md:py-10 md:px-16 flex flex-col items-center gap-8'>
      <div className='flex flex-col items-center mt-4 mb-2 px-4 sm:mt-6 mb-4 text-center'>
        <p className='text-primary font-bold text-sm sm:text-base'>
          {t('consultancy:page_title')}
        </p>
        <Typography variant='h3' className='text-text-primary font-medium text-2xl md:text-xl mt-2'>
          {t('consultancy:page_subtitle')}
        </Typography>
      </div>

      <div className="w-full">
        {loading ? (
          <div className='w-full h-auto flex items-center justify-center m-auto'>
            <img src={Loader} alt='Loading' style={{ width: '4rem', height: 'auto', objectFit: 'cover' }} />
          </div>
        ) : !loading && sortedConsultancies.length > 0 ? (
          <div className="bg-white rounded-md shadow-lg">
            <div className="w-full overflow-x-auto">
              <TableContainer component="div" className="rounded-md">
                <Table
                  size="medium"
                  aria-label="Consultancy table"
                  sx={{
                    '& thead th:nth-of-type(n+2)': { textAlign: 'center' },
                    '& tbody td:nth-of-type(n+2)': { textAlign: 'center' },
                  }}
                >
                  <TableHead className='bg-[#F9FAFB]'>
                    <TableRow>
                      <TableCell className="uppercase text-xs text-text-tertiary font-semibold" align='left'>
                        {t('consultancy:consultancy_table_header_pinner')}
                      </TableCell>
                      <TableCell className="uppercase text-xs text-text-tertiary font-semibold" align='center'>
                        {t('consultancy:consultancy_table_header_plan')}
                      </TableCell>
                      <TableCell className="uppercase text-xs text-text-tertiary font-semibold" align='center'>
                        {t('consultancy:consultancy_table_header_date')}
                      </TableCell>
                      <TableCell className="uppercase text-xs text-text-tertiary font-semibold" align='center'>
                        {t('consultancy:consultancy_table_header_contact')}
                      </TableCell>
                      <TableCell className="uppercase text-xs text-text-tertiary font-semibold" align='center'>
                        {t('consultancy:consultancy_table_header_status')}
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {sortedConsultancies.map((item) => {
                      const formattedDate = formatDate(item.purchased_at, i18n.language);
                      const planStyle = getPlanColor(item.pinnerpro_consultancy.type);
                      const planLabel = getPlanLabel(item.pinnerpro_consultancy.type);
                      const fullName = `${item.user.first_name} ${item.user.last_name}`;

                      return (
                        <TableRow key={item.id} hover>
                          {/* Pinner */}
                          <TableCell>
                            <div className="flex items-center gap-3 min-w-[220px]">
                              <Avatar
                                src={item.user.profile_picture}
                                alt={fullName}
                                sx={{
                                  width: { xs: 40, sm: 45 },
                                  height: { xs: 40, sm: 45 }
                                }}
                              />
                              <div className="flex flex-col">
                                <span className="font-medium text-sm sm:text-base text-text-primary">
                                  {fullName}
                                </span>
                                <span className="font-regular text-xs sm:text-sm text-text-tertiary">
                                  {item.user.email}
                                </span>
                              </div>
                            </div>
                          </TableCell>

                          {/* Piani */}
                          <TableCell className="min-w-[120px]">
                            <Chip
                              label={planLabel}
                              size="small"
                              sx={{
                                backgroundColor: planStyle.backgroundColor,
                                color: planStyle.color,
                                fontWeight: 'bold',
                                padding: '0.5rem',
                                fontSize: { xs: '0.75rem', sm: '0.8rem' }
                              }}
                            />
                          </TableCell>

                          {/* Data */}
                          <TableCell className="text-xs sm:text-sm text-text-tertiary font-medium min-w-[100px]">
                            {formattedDate}
                          </TableCell>

                          {/* Contatta */}
                          <TableCell className="min-w-[90px]">
                            <IconButton
                              size="small"
                              sx={{
                                color: '#E9495A'
                              }}
                              aria-label={`Invia email a ${fullName}`}
                            >
                              <a href={`mailto:${item.user.email}`} target="_blank" rel="noopener noreferrer">
                                <EmailIcon fontSize="medium" />
                              </a>
                            </IconButton>
                          </TableCell>

                          {/* Stato */}
                          <TableCell className="min-w-[140px]">
                            {item.status === ConsultancyStatus.COMPLETED ? (
                              <Typography variant="body2" className="text-dark font-bold">
                                {item.amount || 0} <span className='font-normal'>{t('consultancy:consultancy_table_status_completed')}</span>
                              </Typography>
                            ) : (
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleStatusClick(item.status, item.id, item.id, item.user.first_name)}
                                sx={{ ...buttonStyle, minWidth: 220, whiteSpace: 'nowrap' }}
                              >
                                {t('consultancy:consultancy_table_status_pending')}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </div>
        ) : !loading && sortedConsultancies.length === 0 ? (
          <div className='w-full h-[40vh] my-4 flex flex-col justify-center items-center gap-4'>
            <Typography variant="body1" className="text-text-primary">
              {t('consultancy:no_data_title')}
            </Typography>
            <Typography variant="body1" className="text-text-primary text-center mb-2">
              {t('consultancy:no_data_subtitle')}
            </Typography>
            <EmptyState className='' />
          </div>
        ) : null}
      </div>

      {/* Modal */}
      {paymentId && (
        <CloseConsultancyModal
          open={open}
          onClose={() => setOpen(false)}
          onConsultancyClosed={handleConsultancyClosed}
          consultancyId={selectedConsultancyId}
          paymentId={paymentId}
          pinner_first_name={pinnerFirstName}
        />
      )}
    </div>
  );
};

export default Consultancy;
